import { useState, useEffect } from 'react';
import { formatFranc } from '../lib/utils';
import { Flame, TrendingUp } from 'lucide-react';
import api from '../lib/api';
import EntityAvatar from './EntityAvatar';
import { Skeleton } from './ui/skeleton';
import { spikeLabel } from '../lib/spikeLabel';

/**
 * TrendingStrip — P6 player-facing "🔥 Trending Now" horizontal scroller.
 *
 * Surfaces the people whose real-world attention is spiking hardest right now
 * (`metadata.trending_score`, see backend/trending.py). Tapping a card opens
 * that person (draft/explore). A "buy-low" discovery lever that turns the
 * invisible X-refresh data into a visible reason to transfer.
 */
export default function TrendingStrip({ onEntityClick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/market/trending-now', { params: { limit: 12 } });
        if (!cancelled) setItems(res.data?.trending || []);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.warn('TrendingStrip fetch failed:', err?.message);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="mb-3" data-testid="trending-strip-loading">
        <div className="flex gap-2 overflow-hidden">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-[92px] w-[120px] rounded-2xl shrink-0" />)}
        </div>
      </div>
    );
  }
  if (!items.length) return null;

  return (
    <div className="mb-4" data-testid="trending-strip">
      <div className="flex items-center gap-1.5 mb-2">
        <Flame className="h-4 w-4 text-orange-500" />
        <span className="text-sm font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
          Trending Now
        </span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
        {items.map((entity) => {
          const spike = spikeLabel(entity);
          return (
            <button
              key={entity.id}
              onClick={() => onEntityClick && onEntityClick(entity.id)}
              data-testid={`trending-strip-item-${entity.id}`}
              className="shrink-0 w-[120px] snap-start bg-card border border-border/60 rounded-2xl p-2.5 text-left hover:border-orange-400/60 hover:shadow-sm transition-all active:scale-[0.98]"
            >
              <div className="flex items-center justify-between mb-1.5">
                <EntityAvatar entity={entity} size={36} />
                <span
                  className="flex items-center gap-0.5 text-[10px] font-semibold text-orange-500 tabular-nums"
                  title="Mentions on X: latest day vs their usual day"
                  data-testid={`trending-strip-spike-${entity.id}`}
                >
                  <TrendingUp className="h-3 w-3" />
                  {spike || '🔥'}
                </span>
              </div>
              <p className="text-xs font-medium truncate">{entity.name}</p>
              <p className="text-[11px] text-muted-foreground tabular-nums">{formatFranc(entity.pricing?.price)}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
